import React, { useEffect, useRef, useState } from "react";

import {
  inputStateForValue,
  isValidVVVOIdentifier,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingPigletPrimaryDataById,
  useMutateFootprintingPigletPrimaryData,
  usePracticeManagementPigletParameters,
  useTranslation,
} from "utils";

import { COUNTRY_CODE, HARD_INPUT_STATES } from "consts";

import { Card, Flex, Form, FormInstance, Input, InputNumber, PageSpin, SanityCheck } from "components";

type PigletYieldFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
};

const PigletYieldForm = ({ primaryDataId, readOnly, farmId, year }: PigletYieldFormProps) => {
  const { t } = useTranslation("footprintingPiglet");
  const formRef = useRef<FormInstance>(null);

  const [vvvoIdentifier, setVVVOIdentifier] = useState<string | null>(null);
  const [averageSalesWeightKg, setAverageSalesWeightKg] = useState<number | null>(null);
  const [averageNumberOfLitters, setAverageNumberOfLitters] = useState<number | null>(null);
  const [averageLitterSize, setAverageLitterSize] = useState<number | null>(null);

  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);
  const pigletPrimaryDataQuery = useGetFootprintingPigletPrimaryDataById(primaryDataId ?? "");
  const pigletPracticeQuery = usePracticeManagementPigletParameters(carbonProgram?.country?.toLowerCase() as PracticeManagement.CountryCode);
  const { mutate: mutatePigletPrimaryData } = useMutateFootprintingPigletPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      vvvoIdentifier: vvvoIdentifier?.toLowerCase() ?? null,
      averageSalesWeightKg,
      averageNumberOfLitters,
      averageLitterSize,
      isSubmitted: false,
    },
    primaryDataId
  );

  const isLoading = [pigletPrimaryDataQuery, pigletPracticeQuery].some(queryIsLoading);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutatePigletPrimaryData({});
      }
    },
    [isLoading, mutatePigletPrimaryData, readOnly]
  );

  useEffect(() => {
    if (pigletPrimaryDataQuery.data && pigletPrimaryDataQuery.isSuccess) {
      setVVVOIdentifier(pigletPrimaryDataQuery.data.vvvoIdentifier ?? null);
      setAverageSalesWeightKg(pigletPrimaryDataQuery.data.averageSalesWeightKg ?? null);
      setAverageNumberOfLitters(pigletPrimaryDataQuery.data.averageNumberOfLitters ?? null);
      setAverageLitterSize(pigletPrimaryDataQuery.data.averageLitterSize ?? null);

      formRef.current?.setFieldsValue({
        vvvoIdentifier: pigletPrimaryDataQuery.data.vvvoIdentifier ?? null,
        averageSalesWeightKg: pigletPrimaryDataQuery.data.averageSalesWeightKg ?? null,
        averageNumberOfLitters: pigletPrimaryDataQuery.data.averageNumberOfLitters ?? null,
        averageLitterSize: pigletPrimaryDataQuery.data.averageLitterSize ?? null,
      });

      const fieldsToValidate = (["vvvoIdentifier", "averageSalesWeightKg", "averageNumberOfLitters", "averageLitterSize"] as const).some(
        (field) => pigletPrimaryDataQuery.data[field] != null
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [pigletPrimaryDataQuery.data, pigletPrimaryDataQuery.isSuccess]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-piglet-yield-form" className="padding-200">
      <Form ref={formRef}>
        <Flex vertical gap={24}>
          {carbonProgram?.country === COUNTRY_CODE.GERMANY ? (
            <Input
              className="hide-error-message"
              disabled={readOnly}
              name={"vvvoIdentifier"}
              dataTestId={"footprinting-piglet-vvvo-identifier-input"}
              label={t("pigletYieldForm.question.vvvoIdentifierLabel")}
              value={vvvoIdentifier ?? undefined}
              initialValue={vvvoIdentifier ?? undefined}
              // Prevent setting empty string
              onChange={(e) => setVVVOIdentifier(e.target.value ? e.target.value : null)}
              validateFirst
              rules={[
                { required: true, type: "string" },
                { validator: (_: unknown, v: string) => (isValidVVVOIdentifier(v) ? Promise.resolve() : Promise.reject()) },
              ]}
              guidanceText={t("pigletYieldForm.question.vvvoIdentifierGuidance")}
            />
          ) : null}

          <Form.Item
            validateStatus={
              averageSalesWeightKg
                ? HARD_INPUT_STATES.includes(inputStateForValue(averageSalesWeightKg, pigletPracticeQuery.data?.averageSaleWeightKg))
                  ? "error"
                  : "success"
                : "success"
            }
          >
            <InputNumber
              disabled={readOnly}
              name="averageSalesWeightKg"
              dataTestId="footprinting-piglet-sales-weight-input"
              label={t("pigletYieldForm.question.averageSalesWeightLabel")}
              suffix={t("pigletYieldForm.question.averageSalesWeightSuffix")}
              value={averageSalesWeightKg}
              initialValue={averageSalesWeightKg}
              onChange={(v) => setAverageSalesWeightKg(v)}
              validateFirst
              guidanceText={pigletPracticeQuery.data?.averageSaleWeightKg}
            />
          </Form.Item>
          {averageSalesWeightKg ? <SanityCheck inputValue={averageSalesWeightKg} inputRange={pigletPracticeQuery.data?.averageSaleWeightKg} /> : null}
          <Form.Item
            validateStatus={
              averageNumberOfLitters
                ? HARD_INPUT_STATES.includes(inputStateForValue(averageNumberOfLitters, pigletPracticeQuery.data?.averageNumberLitters))
                  ? "error"
                  : "success"
                : "success"
            }
          >
            <InputNumber
              disabled={readOnly}
              name="averageNumberOfLitters"
              dataTestId="footprinting-piglet-litters-input"
              label={t("pigletYieldForm.question.averageNumberOfLittersLabel")}
              value={averageNumberOfLitters}
              initialValue={averageNumberOfLitters}
              onChange={(v) => setAverageNumberOfLitters(v)}
              validateFirst
              guidanceText={pigletPracticeQuery.data?.averageNumberLitters}
              rules={[
                {
                  required: true,
                  message: t("pigletYieldForm.error.averageNumberOfLitters", {
                    min: pigletPracticeQuery.data?.averageNumberLitters?.[1],
                    max: pigletPracticeQuery.data?.averageNumberLitters?.[2],
                  }),
                },
                { type: "number" },
              ]}
            />
          </Form.Item>
          {averageNumberOfLitters ? <SanityCheck inputValue={averageNumberOfLitters} inputRange={pigletPracticeQuery.data?.averageNumberLitters} /> : null}
          <Form.Item
            validateStatus={
              averageLitterSize
                ? HARD_INPUT_STATES.includes(inputStateForValue(averageLitterSize, pigletPracticeQuery.data?.averageLitterSizePigsPerLitter))
                  ? "error"
                  : "success"
                : "success"
            }
          >
            <InputNumber
              disabled={readOnly}
              name="averageLitterSize"
              dataTestId="footprinting-piglet-litter-size-input"
              label={t("pigletYieldForm.question.averageLitterSizeLabel")}
              suffix={t("pigletYieldForm.question.averageLitterSizeSuffix")}
              value={averageLitterSize}
              initialValue={averageLitterSize}
              onChange={(v) => setAverageLitterSize(v)}
              validateFirst
              guidanceText={pigletPracticeQuery.data?.averageLitterSizePigsPerLitter}
              rules={[
                {
                  required: true,
                  message: t("pigletYieldForm.error.averageLitterSize", {
                    min: pigletPracticeQuery.data?.averageLitterSizePigsPerLitter?.[1],
                    max: pigletPracticeQuery.data?.averageLitterSizePigsPerLitter?.[2],
                  }),
                },
                { type: "number" },
              ]}
            />
          </Form.Item>
          {averageLitterSize ? <SanityCheck inputValue={averageLitterSize} inputRange={pigletPracticeQuery.data?.averageLitterSizePigsPerLitter} /> : null}
        </Flex>
      </Form>
    </Card>
  );
};

PigletYieldForm.Key = "piglet.yield";
export { PigletYieldForm };
